import { useMemo } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import {
  buildQueryArraySorters,
  RecurringTaskDto,
  TagType,
  TaskDto,
  useInfiniteTags,
} from '@sib/api';
import { DEFAULT_PAGE } from '@sib/api/constants';
import { useTranslation } from '@sib/i18n';
import { TagDropdownOption } from '@sib/types';
import { ComboboxTagOptionProps } from '@sib/ui/types';

export const useTagsAutocomplete = ({
  search,
  filters,
}: {
  search?: string;
  filters?: string[];
}) => {
  const { t } = useTranslation();

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    error,
    isLoading: isLoadingTags,
  } = useInfiniteTags({
    page: DEFAULT_PAGE,
    size: 5,
    searchTerm: search,
    filter: filters,
    sort: buildQueryArraySorters([
      {
        field: 'name',
        order: 'asc',
      },
    ]),
  });

  const [sentryRef] = useInfiniteScroll({
    loading: isFetchingNextPage,
    hasNextPage,
    disabled: !!error,
    rootMargin: '0px 0px 0px 0px',
    onLoadMore: () => !isFetchingNextPage && fetchNextPage(),
  });

  const options: ComboboxTagOptionProps[] = useMemo(() => {
    const allTags = data?.pages?.flatMap(page => page.items) ?? [];

    return allTags.map(tag => ({
      label: tag.name,
      value: tag.uuid,
      color: tag.color,
      isPredefined: tag.isPredefined,
    }));
  }, [data]);

  const totalItems = data?.pages?.[0]?.totalItems ?? 0;
  const isLoading = isFetchingNextPage && !data;

  return {
    options,
    sentryRef,
    hasNextPage,
    isLoading,
    isLoadingTags,
    totalItems,
    emptyMessage: t('notSelected'),
    noResultMessage: t('noResults'),
    placeholder: t('components.taskForm.fields.tag.placeholder'),
    clearText: t('clear'),
  };
};

export const useTagOptions = (task: RecurringTaskDto | TaskDto) =>
  useMemo(() => {
    const tags = task.tags ?? [];

    const employeeOptions: TagDropdownOption[] = tags
      .filter(tag => tag.type === TagType.Employee && tag.employee)
      .map(tag => ({
        id: tag.employee!.id,
        name: tag.employee!.name,
        color: 'var(--color-secondary)',
        type: TagType.Employee,
        details: {
          email: tag.employee!.email || '',
          title: tag.employee!.title || '',
          phone: tag.employee!.contactPhoneNumber || '',
        },
        ...(tag.employee?.deletedAt && { deletedAt: tag.employee?.deletedAt }),
      }));

    const contactOptions: TagDropdownOption[] = tags.flatMap(tag =>
      tag.type === TagType.Contact && tag.contact
        ? [
            {
              id: tag.contact.id,
              name: tag.contact.name || tag.contact.email || '',
              color: 'var(--color-secondary)',
              type: TagType.Contact,
              details: {
                email: tag.contact.email || '',
                type: tag.contact.title || '',
              },
            },
          ]
        : []
    );

    const customTags = tags.filter(
      tag => tag.type === TagType.Custom && tag.tag
    );

    const customOptions: TagDropdownOption[] = customTags
      .filter(({ tag }) => !tag!.isPredefined)
      .map(({ tag }) => ({
        id: tag!.uuid,
        name: tag!.name,
        color: tag!.color,
        type: TagType.Custom,
      }));

    const predefinedOptions: TagDropdownOption[] = tags.flatMap(
      ({ tag, type }) =>
        type === TagType.Custom && tag?.isPredefined
          ? [
              {
                id: tag.uuid,
                name: tag.name,
                color: tag.color,
                type: TagType.Custom,
              },
            ]
          : []
    );

    return [
      ...customOptions,
      ...predefinedOptions,
      ...contactOptions,
      ...employeeOptions,
    ];
  }, [task.tags]);
